import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, InputLabel, useMediaQuery } from '@mui/material';
import { DropdownOption } from '../ui/dropdown';
import CategoryPicker from '../category-picker';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

export interface AddContributionModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: { name: string; category: string }) => void;
    isEdit?: { id?: number | null, state: boolean };
    categoryOptions?: DropdownOption[];
}

const AddContributionModal: React.FC<AddContributionModalProps> = ({ open, onClose, onConfirm, isEdit }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [errors, setErrors] = useState<{ name?: string; category?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalName, setOriginalName] = useState('');
    const [originalCategory, setOriginalCategory] = useState('');

    const [fetchContribution, setFetchContribution] = useApi();

    React.useEffect(() => {
        if (isEdit?.state && isEdit.id) {
            const { url, method } = buildApiRequestConfig(routes.contributions.fetchById, [{ param: 'id', value: isEdit.id }]);
            setFetchContribution({ url, method });
        }
    }, [isEdit, open]);

    // Reset form when opening for new contribution (not edit mode)
    React.useEffect(() => {
        if (open && !isEdit?.state) {
            setName('');
            setCategory('');
            setErrors({});
            setIsSubmitting(false);
        }
    }, [open, isEdit?.state]);

    React.useEffect(() => {
        const { data, isLoading } = fetchContribution;
        if (data && !isLoading) {
            setName(data.name);
            setCategory(data.categoryId);
            // Store original values for change detection
            setOriginalName(data.name);
            setOriginalCategory(data.categoryId);
        }
    }, [fetchContribution]);

    // Check if any changes have been made in edit mode
    const hasChanges = React.useMemo(() => {
        if (!isEdit?.state) return true; // Always allow create operation
        
        return (
            name !== originalName ||
            category !== originalCategory
        );
    }, [isEdit?.state, name, originalName, category, originalCategory]);
    
    const validate = () => {
        const newErrors: { name?: string; category?: string } = {};
        if (!name) {
            newErrors.name = 'Contribution name is required';
        }
        if (!category) {
            newErrors.category = 'Category is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleConfirm = async () => {
        if (!validate() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            onConfirm({ name, category });
        } finally {
            // Reset submitting state after a delay to prevent rapid consecutive clicks
            setTimeout(() => setIsSubmitting(false), 1000);
        }
    };

    const handleClose = () => {
        setName('');
        setCategory('');
        setErrors({});
        setIsSubmitting(false);
        setOriginalName('');
        setOriginalCategory('');
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 2 }}>{isEdit?.state ? 'Edit' : 'Add New'} Contribution</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box>
                    <InputLabel sx={{ fontSize: 14 }}>Contribution Name <span style={{ color: 'red' }}>*</span></InputLabel>
                    <TextField
                        fullWidth
                        placeholder="Enter contribution name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        size="small"
                        error={!!errors.name}
                        helperText={errors.name}
                        sx={{ mb: 2}}
                    />
                </Box>
                <Box>
                    <InputLabel sx={{ fontSize: 14 }}>Category <span style={{ color: 'red' }}>*</span></InputLabel>
                    <CategoryPicker
                        value={category ? String(category) : ''}
                        onChange={(val: string) => setCategory(val)}
                        placeholder="Select a category"
                        error={!!errors.category}
                        helperText={errors.category}
                        height={40}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none', background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained" 
                    disabled={isSubmitting || !hasChanges}
                    sx={{ 
                        background: (isSubmitting || !hasChanges) ? '#ccc' : 'var(--color-primary)', 
                        color: '#fff', 
                        boxShadow: 'none', 
                        borderRadius: 2, 
                        fontWeight: 600, 
                        whiteSpace: 'nowrap',
                        '&:hover': { 
                            background: (isSubmitting || !hasChanges) ? '#ccc' : 'var(--color-primary)' 
                        },
                        '&:disabled': {
                            background: '#ccc',
                            color: 'var(--color-text-muted)'
                        }
                    }}
                >
                    {isSubmitting ? 'Processing...' : isEdit?.state ? 'Save Changes' : 'Add Contribution'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4, p: 0, overflowY: 'hidden' } }}>
            {content}
        </Dialog>
    );
};

export default AddContributionModal;
